import { c as changeItemQuantity, a as contexts } from './cart-cce84c00.js';
import { E as EVENTS } from './events-58bc9098.js';

class CartItem extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      changePending: "change-pending",
      deletePending: "delete-pending",
    };

    this.selectors = {
      errorDisplay: "[js-error-display]",
      deleteButton: "[js-delete-button]",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();

    const eventOptions = {
      signal: this.controller.signal,
    };

    this.cartItemKey = this.dataset.cartItemKey;

    // Because the cart re-renders cart items on any update, we can
    // use the data attribute to know the last valid quantity.  This
    // is necessary to revert things on quantity change error
    this.lastConfirmedQuantity = this.dataset.cartItemQuantity;

    this.quantityInput = this.querySelector("quantity-input");
    this.quantityInput.addEventListener(
      EVENTS.QUANTITY_INPUT_CHANGE,
      (event) => {
        this.dispatchEvent(
          new CustomEvent(EVENTS.CART_ITEM_CHANGE_PENDING, {
            bubbles: true,
          }),
        );

        this.classList.add(this.classes.changePending);
        const batchUpdatePromise = this.#syncRelatedFreeGiftsQuantity(
          event.detail.quantity,
        );
        if (!batchUpdatePromise) {
          changeItemQuantity(
            this,
            this.cartItemKey,
            event.detail.quantity,
            contexts.cartDrawer,
          );
        } else {
          batchUpdatePromise
            .then((didUpdate) => {
              if (!didUpdate) {
                changeItemQuantity(
                  this,
                  this.cartItemKey,
                  event.detail.quantity,
                  contexts.cartDrawer,
                );
              }
            })
            .catch(() => {
              changeItemQuantity(
                this,
                this.cartItemKey,
                event.detail.quantity,
                contexts.cartDrawer,
              );
            });
        }
      },
      eventOptions,
    );

    this.deleteButton = this.querySelector(this.selectors.deleteButton);

    this.deleteButton.addEventListener(
      "click",
      () => {
        this.classList.add(this.classes.deletePending);
        this.dispatchEvent(
          new CustomEvent(EVENTS.CART_ITEM_CHANGE_PENDING, {
            bubbles: true,
          }),
        );
        const batchRemovalPromise = this.#removeRelatedFreeGifts();
        if (!batchRemovalPromise) {
          changeItemQuantity(
            this,
            this.cartItemKey,
            0,
            contexts.cartDrawer,
          );
        } else {
          batchRemovalPromise
            .then((didUpdate) => {
              if (!didUpdate) {
                changeItemQuantity(
                  this,
                  this.cartItemKey,
                  0,
                  contexts.cartDrawer,
                );
              }
            })
            .catch(() => {
              changeItemQuantity(
                this,
                this.cartItemKey,
                0,
                contexts.cartDrawer,
              );
            });
        }
      },
      eventOptions,
    );

    this.addEventListener(
      EVENTS.CART_ITEM_CHANGE_ERROR,
      (event) => {
        if (this.cartItemKey === event.detail.cartItemKeyOrId) {
          this.#displayErrorMessage(event.detail.errorMessage);
          this.classList.remove(this.classes.changePending);
          if (this.lastConfirmedQuantity !== undefined) {
            this.quantityInput.setQuantity(this.lastConfirmedQuantity);
          }
        }
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  #displayErrorMessage(message) {
    this.errorDisplay = this.querySelector(this.selectors.errorDisplay);
    this.errorDisplay.hidden = false;
    this.errorDisplay.innerText = message;
  }

  #removeRelatedFreeGifts() {
    return this.#updateRelatedFreeGiftsQuantity(0);
  }

  #updateItemsByKey(updates) {
    const routes = window.theme?.routes?.cart || {};
    const updatePath = `${routes.update || '/cart/update'}.js`;

    return fetch(updatePath, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 422) {
          const errorEvent = new CustomEvent(EVENTS.CART_ITEM_CHANGE_ERROR, {
            detail: {
              cartItemKeyOrId: this.cartItemKey,
              errorMessage: data.description,
            },
          });
          this.dispatchEvent(errorEvent);
          document.dispatchEvent(
            new CustomEvent(EVENTS.CART_ERROR, {
              detail: { errorMessage: data.description },
            }),
          );
          throw new Error(data.description);
        }

        const eventOptions = {
          detail: {
            cartData: data,
          },
          bubbles: true,
        };

        this.dispatchEvent(new CustomEvent(EVENTS.CART_ITEM_CHANGED, eventOptions));
        document.dispatchEvent(new CustomEvent(EVENTS.CART_CHANGED, eventOptions));
        return data;
      });
  }

  #syncRelatedFreeGiftsQuantity(targetQuantity) {
    return this.#updateRelatedFreeGiftsQuantity(targetQuantity);
  }

  #updateRelatedFreeGiftsQuantity(targetQuantity) {
    if (this.dataset.freeGiftParent !== 'true') {
      return null;
    }

    const groupId = this.dataset.freeGiftGroupId || null;
    const parentVariantId = this.dataset.freeGiftParentVariantId || null;
    const routes = window.theme?.routes?.cart || {};
    const cartPath = `${routes.base || '/cart'}.js`;

    return fetch(cartPath, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((cart) => {
        const updates = {};

        cart.items.forEach((item) => {
          if (item.properties?._free_gift_child !== 'true') {
            return;
          }

          const itemGroupId = item.properties?._free_gift_group_id || null;
          const itemParentVariantId =
            item.properties?._free_gift_parent_variant_id || null;
          const matchesGroupId = groupId && itemGroupId === groupId;
          const matchesParentVariantId =
            !groupId &&
            parentVariantId &&
            itemParentVariantId === parentVariantId;

          if (!matchesGroupId && !matchesParentVariantId) {
            return;
          }

          updates[item.key] = targetQuantity;
        });

        if (Object.keys(updates).length === 0) {
          return false;
        }

        updates[this.cartItemKey] = targetQuantity;
        return this.#updateItemsByKey(updates).then(() => true);
      });
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}
